/**
 * src/lib/consent.ts
 * LGPD Consent helpers — D-23..D-26 (Phase 2 foundational gate).
 *
 * Pure module — NO React imports.
 * Downstream consumers (plans 2.3/2.5/2.9) import getConsent() / onConsentChange()
 * to gate third-party tracker lazy-loading.
 */

/** D-25: Cookie name and Max-Age (180 dias = 15 552 000 segundos) */
const COOKIE_NAME = "consent";
const COOKIE_MAX_AGE = 60 * 60 * 24 * 180; // 15552000
const COOKIE_VERSION = 4 as const;

/**
 * D-25: ConsentPrefs — shape persistida no cookie JSON.
 * version: 4 — bump 3→4 (2026-08-08) invalida consents salvos. Com o ConsentBanner
 * removido (atrapalhava o CTA), isso garante que quem tinha aceitado antes também
 * volte a "só essenciais": getConsent() vira null → Clarity/Fomo/PostHog não carregam.
 */
export type ConsentPrefs = {
  analytics: boolean;
  marketing: boolean;
  version: typeof COOKIE_VERSION;
  decidedAt: string; // ISO-8601
};

/**
 * Subscriber list — module-level Set (D-25).
 * T-2.1-05: cada subscriber é invocado em try/catch; falha de um não quebra os demais.
 */
const subscribers = new Set<(prefs: ConsentPrefs | null) => void>();

// ---------------------------------------------------------------------------
// Cookie primitives (D-25 — document.cookie direto, sem biblioteca)
// ---------------------------------------------------------------------------

/** Lê cookie por nome; retorna null se não encontrado ou SSR. */
function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

/** Escreve cookie com Path=/ e SameSite=Lax (D-25). */
function writeCookie(name: string, value: string, maxAge: number): void {
  if (typeof document === "undefined") return;
  document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${maxAge}; Path=/; SameSite=Lax`;
}

/** Remove cookie setando Max-Age=0 (D-25 / D-26). */
function deleteCookie(name: string): void {
  if (typeof document === "undefined") return;
  document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax`;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * D-25: Retorna ConsentPrefs se uma decisão já foi registrada, null caso contrário.
 * Retorna null se o cookie estiver malformado ou em version incompatível (T-2.1-01).
 */
export function getConsent(): ConsentPrefs | null {
  const raw = readCookie(COOKIE_NAME);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<ConsentPrefs>;
    // Invalidação por versão — permite futuro bump de formato (T-2.1-01)
    if (parsed.version !== COOKIE_VERSION) return null;
    if (typeof parsed.analytics !== "boolean" || typeof parsed.marketing !== "boolean") return null;
    if (typeof parsed.decidedAt !== "string") return null;
    return parsed as ConsentPrefs;
  } catch {
    return null;
  }
}

/**
 * D-25: Persiste decisão de consent e notifica subscribers.
 * D-26: Executa limpeza de cookies quando categoria muda de true → false.
 */
export function setConsent(input: { analytics: boolean; marketing: boolean }): void {
  const previous = getConsent();
  const next: ConsentPrefs = {
    analytics: input.analytics,
    marketing: input.marketing,
    version: COOKIE_VERSION,
    decidedAt: new Date().toISOString(),
  };
  writeCookie(COOKIE_NAME, JSON.stringify(next), COOKIE_MAX_AGE);

  // D-26: deletar cookies de categorias revogadas (true → false)
  if (previous?.analytics === true && next.analytics === false) clearAnalyticsCookies();
  if (previous?.marketing === true && next.marketing === false) clearMarketingCookies();

  // Notificar subscribers (T-2.1-05: try/catch por subscriber)
  for (const cb of subscribers) {
    try {
      cb(next);
    } catch (err) {
      if (import.meta.env.DEV) {
        console.warn("[consent] subscriber error:", err);
      }
    }
  }
}

/**
 * D-25: Registra callback que será invocado a cada mudança de consent.
 * Retorna função de unsubscribe (cleanup para useEffect).
 */
export function onConsentChange(
  cb: (prefs: ConsentPrefs | null) => void
): () => void {
  subscribers.add(cb);
  return () => {
    subscribers.delete(cb);
  };
}

/**
 * D-26: Remove cookies de Analytics (Microsoft Clarity) setando Max-Age=0.
 * Chamado automaticamente por setConsent quando analytics muda true → false.
 */
export function clearAnalyticsCookies(): void {
  deleteCookie("_clck");
  deleteCookie("_clsk");
}

/**
 * D-26: Remove cookies de Marketing (GA4, Meta Pixel, Fomo).
 * Iteração sobre document.cookie para capturar sufixos dinâmicos (_ga_*, _fomo_*).
 * Chamado automaticamente por setConsent quando marketing muda true → false.
 */
export function clearMarketingCookies(): void {
  deleteCookie("_ga");
  deleteCookie("_fbp");
  if (typeof document === "undefined") return;
  // Varre cookies carregados para remover prefixos dinâmicos
  for (const part of document.cookie.split(";")) {
    const name = part.split("=")[0]?.trim();
    if (!name) continue;
    if (name.startsWith("_fomo_") || name.startsWith("_ga_")) {
      deleteCookie(name);
    }
  }
}

// ---------------------------------------------------------------------------
// Fomo Social-Proof loader (Plan 2.5 / D-13 / D-14 / PROOF-01)
// ---------------------------------------------------------------------------

declare global {
  interface Window {
    __fomoLoaded?: boolean;
    __clarityLoaded?: boolean;
  }
}

/**
 * D-13/D-14/D-15 — Lazy-injeta o script do Fomo no <head>.
 * Idempotente: verifica window.__fomoLoaded antes de qualquer ação.
 * Só executa quando marketing consent === true (chamada via onConsentChange subscriber).
 * T-2.5-03: idempotência via flag previne re-injeção em múltiplos consent changes.
 */
export function loadFomo(): void {
  if (typeof window === "undefined") return;
  if (window.__fomoLoaded) return;
  const key = import.meta.env.VITE_FOMO_KEY as string | undefined;
  if (!key || key === "fomo-XXXXXXXX") return; // No-op sem chave real (dev/preview sem config)
  const script = document.createElement("script");
  script.async = true;
  // Loader URL padrão Fomo — confirmar via Fomo dashboard durante execute-phase se necessário
  script.src = `https://load.fomo.com/api/v1/${key}.js`;
  document.head.appendChild(script);
  window.__fomoLoaded = true;
}

// ---------------------------------------------------------------------------
// Microsoft Clarity loader (Plan 2.9 / D-21 / D-22 / CLARITY-01)
// ---------------------------------------------------------------------------

/**
 * D-21/D-22 — Microsoft Clarity lazy injection. Idempotente. Only call when analytics consent === true.
 * Single injection method: <script async src="..."> appended to <head>. Mirrors loadFomo() pattern.
 * NÃO usar a snippet IIFE inline (oficial Clarity docs) — duplica o trabalho.
 * T-2.9-03: idempotência via window.__clarityLoaded flag previne re-injeção em múltiplos consent changes.
 */
export function loadClarity(): void {
  if (typeof window === "undefined") return;
  if (window.__clarityLoaded) return;
  const id = import.meta.env.VITE_CLARITY_PROJECT_ID as string | undefined;
  if (!id || id === "XXXXXXXXXX") return; // No-op sem ID real (dev/preview sem config)

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.clarity.ms/tag/${id}`;
  document.head.appendChild(script);

  window.__clarityLoaded = true;
}

// ---------------------------------------------------------------------------
// PostHog lazy loader (Plan 03-07 / D-30)
// ---------------------------------------------------------------------------
import { loadPostHog } from "./posthog";

// ---------------------------------------------------------------------------
// Top-level subscriber: wires consent changes → tracker loaders (Phase 2 + 3)
// Plans 2.5 (Fomo/marketing), 2.9 (Clarity/analytics), 03-07 (PostHog/analytics) hook aqui.
// Executado uma vez no primeiro import do módulo (D-21 pattern).
// ---------------------------------------------------------------------------

if (typeof window !== "undefined") {
  // Verificação inicial: se já há consent salvo, carrega trackers imediatamente
  const _initialConsent = getConsent();
  if (_initialConsent?.marketing) loadFomo();
  if (_initialConsent?.analytics) loadClarity();
  if (_initialConsent?.analytics) void loadPostHog(); // D-30 — PostHog lazy load post-consent

  // Subscription: dispara loaders sempre que as categorias flipam para true
  onConsentChange((prefs) => {
    if (prefs?.marketing) loadFomo();
    if (prefs?.analytics) loadClarity();
    if (prefs?.analytics) void loadPostHog(); // D-30 — PostHog analytics gate
    // Revoke path: D-21 — não descarrega scripts (requer reload).
    // clearMarketingCookies() já remove _fomo_* cookies quando marketing → false.
    // clearAnalyticsCookies() já remove _clck + _clsk quando analytics → false.
  });
}
